import { Fragment, memo, useCallback, useMemo } from 'react';

import { flip, offset, shift, useFloating } from '@floating-ui/react-dom';
import { Menu } from '@headlessui/react';
import { FormattedMessage } from 'localization';
import { twMerge } from 'tailwind-merge';

import useGetSupportedLanguages from '../../hooks/useGetSupportedLanguages';
import { DropdownItem } from '../Dropdown/DropdownItem';
import { DropdownPopup } from '../Dropdown/DropdownPopup';
import { AgentPanelButton } from './AgentPanelButton';

export interface ITicketLanguagePanel {
  /** Current ticket language (ISO code, e.g. "en"). */
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Ticket language selector — mirrors AssignAgentPanel: a button showing the
 * current language (flag + label) that opens a dropdown of supported languages
 * with flag icons. Lets agents correct the auto-detected ticket language.
 */
export function TicketLanguagePanel({
  value,
  onChange,
  disabled,
  className,
}: ITicketLanguagePanel) {
  const languages = useGetSupportedLanguages();

  const current = useMemo(() => languages.find((l) => l.value === value), [languages, value]);

  const handleChange = useCallback(
    (next: string) => {
      if (next !== value) onChange?.(next);
    },
    [onChange, value],
  );

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift()],
  });

  return (
    <Menu
      ref={refs.setReference}
      as="div"
      className={twMerge('relative z-1 min-w-[140px] max-w-[188px]', className)}
    >
      {({ open }) => (
        <>
          <Menu.Button disabled={disabled} as={Fragment}>
            <div className="relative">
              <AgentPanelButton
                logo={current?.icon}
                open={open}
                title={
                  <span className="overflow-hidden overflow-ellipsis">
                    {current ? current.label : '—'}
                  </span>
                }
                subtitle={<FormattedMessage id="desk-app.language" />}
              />
            </div>
          </Menu.Button>

          <DropdownPopup
            ref={refs.setFloating}
            style={floatingStyles}
            className="static m-0 max-h-[70vh] overflow-y-auto"
            classNameWrapper="w-full"
            position="bottom-start"
            open={open}
          >
            <div className="flex flex-col w-full gap-[5px] py-2">
              {languages.map((language) => (
                <DropdownItem
                  key={language.value}
                  onClick={() => handleChange(language.value)}
                  className="px-3"
                  selected={language.value === value}
                >
                  <div className="flex gap-2 items-center w-full">
                    <span className="flex items-center shrink-0">{language.icon}</span>
                    <div className="text-sm overflow-hidden overflow-ellipsis">
                      {language.label}
                    </div>
                  </div>
                </DropdownItem>
              ))}
            </div>
          </DropdownPopup>
        </>
      )}
    </Menu>
  );
}

export default memo<ITicketLanguagePanel>(TicketLanguagePanel);
